'use client'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SandboxEngineIndicator } from '../primitives/engine-indicator'
import { TrendChip } from '../primitives/trend-chip'
import { AiResponseExcerpt } from '../primitives/ai-response-excerpt'
import { EngineLogo } from '@/components/dashboard/engine-logo'
import { cn } from '@/lib/utils'
import type { SandboxPrompt } from '../data/types'
import type { Engine } from '@/lib/ai/constants'

const ENGINES: Engine[] = ['chatgpt', 'perplexity', 'google_aio']
const ENGINE_LABELS: Partial<Record<Engine, string>> = {
  chatgpt: 'ChatGPT',
  perplexity: 'Perplexity',
  google_aio: 'Google AI Overview',
}

interface PromptRowProps {
  prompt: SandboxPrompt
  isExpanded: boolean
  onToggle: () => void
}

function PromptRow({ prompt, isExpanded, onToggle }: PromptRowProps) {
  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-b border-border transition-colors duration-150 hover:bg-muted/50',
          isExpanded && 'bg-muted/30'
        )}
        onClick={onToggle}
      >
        {/* The disclosure is a real button in the first cell (its click bubbles to the row's
            pointer handler, so the whole row still toggles by mouse): a `tr role=button` would nest
            the engine tooltip triggers inside another interactive element (axe nested-interactive). */}
        <td className="py-2 pl-3 pr-2 text-xs text-foreground">
          <button
            type="button"
            className="focus-ring rounded-sm text-left"
            aria-expanded={isExpanded}
          >
            {prompt.text}
          </button>
        </td>
        <td className="py-2 px-2">
          <div className="flex items-center gap-3">
            {ENGINES.map((engine) => (
              <SandboxEngineIndicator
                key={engine}
                engine={engine}
                status={prompt.status[engine] ?? 'not-visible'}
              />
            ))}
          </div>
        </td>
        <td className="py-2 px-2">
          <TrendChip value={prompt.trend} />
        </td>
        <td className="py-2 pl-2 pr-3 text-label text-muted-foreground">{prompt.lastScanned}</td>
      </tr>

      {/* Expanded panel */}
      <tr>
        <td
          colSpan={4}
          className={cn(
            'overflow-hidden border-b border-border bg-surface transition-all duration-300',
            isExpanded ? 'py-3 px-4' : 'py-0 px-4'
          )}
          style={{ display: isExpanded ? 'table-cell' : 'none' }}
          aria-live="polite"
        >
          {isExpanded && (
            <Tabs defaultValue="chatgpt">
              <TabsList className="mb-3 h-7">
                {ENGINES.map((engine) => (
                  <TabsTrigger key={engine} value={engine} className="gap-1 text-xs px-2 py-1 h-6">
                    <EngineLogo engine={engine} size={12} />
                    {ENGINE_LABELS[engine]}
                  </TabsTrigger>
                ))}
              </TabsList>
              {ENGINES.map((engine) => {
                const response = prompt.responses[engine]
                if (!response) return null
                return (
                  <TabsContent key={engine} value={engine}>
                    <AiResponseExcerpt excerpt={response} />
                  </TabsContent>
                )
              })}
            </Tabs>
          )}
        </td>
      </tr>
    </>
  )
}

interface PromptsPageProps {
  prompts: SandboxPrompt[]
  expandedRowId: string | null
  onToggleRow: (id: string) => void
}

export function PromptsPage({ prompts, expandedRowId, onToggleRow }: PromptsPageProps) {
  const sorted = [...prompts].sort((a, b) => b.visibilityScore - a.visibilityScore)

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[460px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="py-1.5 pl-3 pr-2 text-left u-label">Prompt</th>
            <th className="py-1.5 px-2 text-left u-label">Engines</th>
            <th className="py-1.5 px-2 text-left u-label">Trend</th>
            <th className="py-1.5 pl-2 pr-3 text-left u-label">Scanned</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((prompt) => (
            <PromptRow
              key={prompt.id}
              prompt={prompt}
              isExpanded={expandedRowId === prompt.id}
              onToggle={() => onToggleRow(expandedRowId === prompt.id ? '' : prompt.id)}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}
